import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useHistory, Link } from 'react-router-dom';
import axios from '../../../../services/AxiosInstance';
import { resolveRoleAccount } from '../../../../services/api/chartRolesApi';
import dayjs from 'dayjs';
import {
  Spinner,
  Alert,
  Card,
  Row,
  Col,
  Badge,
  Button,
  OverlayTrigger,
  Tooltip,
  Dropdown,
} from 'react-bootstrap';
import swal from 'sweetalert';
import Swal from 'sweetalert2';
import { toast } from 'sonner';

import { Worker, Viewer } from '@react-pdf-viewer/core';
import '@react-pdf-viewer/core/lib/styles/index.css';

// Lucide React Icons
import {
  FileText,
  Calendar,
  DollarSign,
  Building,
  Clock,
  CheckCircle,
  AlertCircle,
  Download,
  Eye,
  Edit,
  Archive,
  CreditCard,
  Receipt,
  FileImage,
  File,
  User,
  Hash,
  CalendarDays,
  Banknote,
  Timer,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  ExternalLink,
  Settings,
  Info,
  Undo2,
    ShoppingCart
} from 'lucide-react';

// Import your API utility function
import { getApiUrl } from '../../../../utils/ApiUtils';
import PageHeader from '../../../layouts/PageHeader';
import StatusBadge from '../../Common/StatusBadge';
import { autoBookingInvoiceBadge } from '../../Common/autoBookingBadge';
import { useCapabilities } from '../../../../context/CapabilityContext';
import { PaymentMethodProposalBanner } from '../../Banking/payments/SupplierPaymentMethods';
import { addToQueue, apiError, paymentFileChip } from '../../Banking/payments/paymentsApi';
import { useReconciliationHistory, describeOnInvoice, canUndo as canUndoBankAction, bankLineHref } from '../../Banking/reconciliation/ReconciliationHistory';
import { useAccountingSettings } from '../../../../context/AccountingSettingsContext';
import { useFeatures } from '../../../../context/FeatureContext';
import {
  handOffState,
  supplierInvoiceDescriptor,
  useSurfaceTaskDescriptor,
} from '../../../../context/TaskContextDescriptor';

// Import custom components
import VerificationModal from './VerificationModal';
import VerificationDetailsModal from '../../Accounting/Verification/VerificationDetailsModal';

const ViewSupplierInvoice = () => {
  const { companyId, invoiceId } = useParams();
  const history = useHistory();
  const { accountingSettings } = useAccountingSettings();

  // State variables
  const [loading, setLoading] = useState(true);
  const [invoice, setInvoice] = useState(null);
  const [attachmentUrls, setAttachmentUrls] = useState({});
  const [error, setError] = useState(null);
  const [markAsPaidLoading, setMarkAsPaidLoading] = useState(false);

  // Modal states
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [showVerificationDetails, setShowVerificationDetails] = useState(false);
  const [selectedVerification, setSelectedVerification] = useState(null);
  const [verificationType, setVerificationType] = useState(null);

  // New state for payment date
  const [paymentDate, setPaymentDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [paymentAmount, setPaymentAmount] = useState(null);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [verifications, setVerifications] = useState([]);

  // Undo state
  const [undoLoading, setUndoLoading] = useState(false);

  const taskDescriptor = useMemo(() => supplierInvoiceDescriptor(invoice), [invoice]);
  useSurfaceTaskDescriptor(taskDescriptor);

  // The contextual assistant entry point rides the same entitlement as the assistant page it
  // navigates to — offering a link to a route the tenant cannot open would be a dead end.
  const { features } = useFeatures();
  const hasAiAssistant = Boolean(features?.ai_assistant);
  const { capabilities } = useCapabilities();
  const isAdmin = Boolean(capabilities?.isAdmin);
  const [queueBusy, setQueueBusy] = useState(false);

  const handleAskAssistant = useCallback(() => {
    if (!taskDescriptor) return;
    history.push(`/${companyId}/ai-assistant`, handOffState(taskDescriptor));
  }, [companyId, history, taskDescriptor]);

  // Fetch helpers (extracted so they can be reused by undo handler)
  const fetchInvoice = useCallback(async () => {
    try {
      const getSupplierInvoiceApiUrl = getApiUrl(
        `${companyId}/purchasing/supplier-invoices/${invoiceId}`
      );
      const response = await axios.get(getSupplierInvoiceApiUrl, {
        withCredentials: true,
      });
      setInvoice(response.data);
    } catch (error) {
      console.error('Error fetching invoice details:', error);
      setError('Kunde inte hämta fakturadetaljer. Vänligen försök igen senare.');
    } finally {
      setLoading(false);
    }
  }, [companyId, invoiceId]);

  const fetchAssociatedVerifications = useCallback(async () => {
    try {
      const getAssociatedVerificationsApiUrl = getApiUrl(
        `${companyId}/purchasing/supplier-invoices/${invoiceId}/verifications`
      );

      const response = await axios.get(getAssociatedVerificationsApiUrl, {
        withCredentials: true,
      });

      setVerifications(response.data);
    } catch (error) {
      console.error('Error fetching associated verifications:', error);
      setError('Kunde inte hämta associerade verifikationer. Vänligen försök igen senare.');
    }
  }, [companyId, invoiceId]);

  // Fetch the invoice details
  useEffect(() => {
    fetchInvoice();
    fetchAssociatedVerifications();
  }, [fetchInvoice, fetchAssociatedVerifications]);

  // Bank reconciliation done on this invoice; undoing it refreshes the page's
  // own data in place.
  const bankHistory = useReconciliationHistory({
    companyId,
    invoiceId,
    onChanged: () => Promise.all([fetchInvoice(), fetchAssociatedVerifications()]),
  });

  // Fetch bank accounts (for accrual payment dialog)
  useEffect(() => {
    const fetchBankAccounts = async () => {
      try {
        const response = await axios.get(
          getApiUrl(`${companyId}/accounting/accounts?scope=company`),
          { withCredentials: true }
        );
        const accounts = response.data.accounts || response.data || [];
        setBankAccounts(accounts.filter(a => {
          if (a.is_payment_account) return true;
          const num = parseInt(a.account_number);
          return num >= 1900 && num < 2000;
        }));
      } catch (err) {
        console.error('Error fetching bank accounts:', err);
      }
    };
    fetchBankAccounts();
  }, [companyId]);

  // Fetch attachments when the invoice changes
  useEffect(() => {
    const fetchAttachments = async () => {
      if (invoice && invoice.attachments && invoice.attachments.length > 0 && invoice.attachments[0].attachment_id) {
        const urls = {};
        for (const attachment of invoice.attachments) {
          try {
            const attachmentUrl = await fetchAttachment(attachment.attachment_id);
            urls[attachment.attachment_id] = attachmentUrl;
          } catch (error) {
            console.error('Error fetching attachment:', error);
            setError('Ett fel uppstod vid hämtning av bilagor.');
          }
        }
        setAttachmentUrls(urls);
      }
    };

    fetchAttachments();
  }, [invoice]);

  // Function to fetch an attachment from the backend and create a Blob URL
  const fetchAttachment = async (attachmentId) => {
    const attachmentApiUrl = getApiUrl(`${companyId}/attachments/${attachmentId}`);
    const response = await axios.get(attachmentApiUrl, {
      withCredentials: true,
      responseType: 'blob',
    });
    const blob = new Blob([response.data], {
      type: response.headers['content-type'],
    });
    const url = URL.createObjectURL(blob);
    return url;
  };

  // Cleanup Blob URLs when component unmounts or attachmentUrls change
  useEffect(() => {
    return () => {
      // Revoke Blob URLs to free memory
      Object.values(attachmentUrls).forEach((url) => {
        URL.revokeObjectURL(url);
      });
    };
  }, [attachmentUrls]);

  const getStatusDisplayName = (status) => {
    const statusNames = {
      'Scanned': 'Faktura inläst',
      'Approved': 'Godkänd',
      'Paid': 'Betald & bokförd'
    };
    return statusNames[status] || status;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'Scanned': { variant: 'info', icon: FileText },
      'Approved': { variant: 'warning', icon: CheckCircle },
      'Paid': { variant: 'success', icon: CreditCard }
    };

    const config = statusConfig[status] || { variant: 'secondary', icon: FileText };
    const IconComponent = config.icon;

    return (
      <Badge
        bg={config.variant}
        className="d-flex align-items-center gap-1"
        style={{ fontSize: '0.85rem', padding: '0.5rem 0.75rem' }}
      >
        <IconComponent size={14} />
        {getStatusDisplayName(status)}
      </Badge>
    );
  };

  const getStatusIcon = (status) => {
    const iconMap = {
      'Scanned': { icon: FileText, color: 'var(--bs-cyan)' },
      'Approved': { icon: CheckCircle, color: 'var(--bs-warning)' },
      'Paid': { icon: CreditCard, color: 'var(--bs-success)' }
    };

    return iconMap[status] || { icon: FileText, color: '#6b7280' };
  };

  // Derive accounting method from the invoice's fiscal year (returned by backend)
  // Falls back to context settings, then to 'accrual' default
  const accountingMethod = invoice?.accounting_method || accountingSettings?.accountingMethod || 'accrual';

  const getNextAction = () => {
    // Determine effective method: accrual companies always use accrual;
    // cash companies use accrual flow when use_accrual_method is toggled on
    const effectiveMethod = (accountingMethod === 'accrual' || (invoice.use_accrual_method || invoice.year_end_booked)) ? 'accrual' : 'cash';

    switch (invoice.status) {
      case 'Scanned':
        if (effectiveMethod === 'accrual') {
          // Accrual: approval opens VerificationModal to book accounts-payable (2440)
          return { text: 'Godkänn & bokför', nextStatus: 'Approved', requireDate: false, isVerification: true, verificationType: 'accrual_invoice' };
        }
        // Cash: simple approval, no verification needed
        return { text: 'Godkänn', nextStatus: 'Approved', requireDate: false };
      case 'Approved':
        if (effectiveMethod === 'accrual') {
          // Accrual: single dialog with date + amount + bank account → auto-generates 2440 entries
          return { text: 'Registrera betalning', nextStatus: 'Paid', requireDate: true, requireBankAccount: true };
        }
        // Cash: payment opens VerificationModal for full booking
        return { text: 'Registrera betalning', nextStatus: 'Paid', requireDate: true, isVerification: true, verificationType: 'cash_payment' };
      case 'Paid':
        // Terminal state
        return null;
      default:
        return null;
    }
  };

  const nextAction = invoice ? getNextAction() : null;

  // Calculate days until/past due date
  const getDaysUntilDue = () => {
    if (!invoice || !invoice.due_date) return null;
    const today = dayjs();
    const dueDate = dayjs(invoice.due_date);
    const days = dueDate.diff(today, 'day');
    return days;
  };

  const daysUntilDue = invoice ? getDaysUntilDue() : null;

  /**
   * Payment ↔ payment-file row (ISO 20022 session 2, plan §3.11 intro): when
   * this invoice has a row sent to the bank and the amount being registered
   * equals the row to the öre, the payment settles the row. Any other amount
   * is registered without a link (the server then only records an event).
   */
  const paymentFileLink = (amountOverride) => {
    if (!invoice?.payment_instruction_id || !['in_file', 'pending', 'accepted'].includes(invoice.payment_instruction_status)) return {};
    const amount = amountOverride !== undefined && amountOverride !== null ? Number(amountOverride) : Number(invoice.remaining_balance ?? invoice.amount);
    if (Math.round(amount * 100) !== Math.round(Number(invoice.payment_instruction_amount) * 100)) return {};
    return { instruction_id: invoice.payment_instruction_id };
  };

  const handleQueueForPayment = async () => {
    setQueueBusy(true);
    try {
      const result = await addToQueue(companyId, { sourceType: 'supplier_invoices', sourceIds: [invoiceId] });
      if (result.queued > 0) toast.success('Fakturan lades i betalningskön.');
      await fetchInvoice();
    } catch (err) {
      const e = apiError(err, 'Kunde inte lägga fakturan i betalningskön.');
      toast.error(e.details?.refused?.[0]?.errors?.join(' ') || e.message);
    } finally {
      setQueueBusy(false);
    }
  };

  const handleStatusUpdate = async (nextStatus, dateOverride, amountOverride, transactionsOverride) => {
    setMarkAsPaidLoading(true);
    setError(null);

    const effectiveDate = dateOverride || paymentDate;

    try {
      let apiUrl;
      let payload;

      if (nextStatus === 'Approved') {
        // Simple approval (cash method — no verification)
        apiUrl = getApiUrl(
          `${companyId}/purchasing/supplier-invoices/${invoiceId}/approve`
        );
        payload = {};
      } else if (nextStatus === 'Paid') {
        // Accrual payment (no modal — backend auto-generates verifikat)
        apiUrl = getApiUrl(
          `${companyId}/purchasing/supplier-invoices/${invoiceId}/pay`
        );
        payload = {
          payment_date: effectiveDate,
          ...(amountOverride !== undefined && amountOverride !== null && { amount: amountOverride }),
          ...(transactionsOverride && { transactions: transactionsOverride }),
          ...paymentFileLink(amountOverride),
        };
      } else {
        throw new Error(`Unknown next status: ${nextStatus}`);
      }

      await axios.post(apiUrl, payload, { withCredentials: true });

      // Re-fetch invoice and verifications from server for accurate state
      await Promise.all([fetchInvoice(), fetchAssociatedVerifications()]);

      // Display success message
      toast.success(`Fakturan har uppdaterats till ${getStatusDisplayName(nextStatus).toLowerCase()}.`);
    } catch (error) {
      console.error('Error updating invoice status:', error);
      const msg = error.response?.data?.error || 'Kunde inte uppdatera fakturastatus. Vänligen försök igen senare.';
      setError(msg);
    } finally {
      setMarkAsPaidLoading(false);
    }
  };

  const handleViewVerification = async (verification) => {
    try {
      // Fetch the full verification details including transactions
      const verificationDetailsUrl = getApiUrl(
        `${companyId}/verification/${verification.verification_id}`
      );
      const response = await axios.get(verificationDetailsUrl, {
        withCredentials: true,
      });

      const data = response.data;

      if (!data || !data.verification_id) {
        setError('Inga verifikationsdetaljer hittades.');
        return;
      }

      // The API returns a structured object with transactions array
      const transactions = (data.transactions || []).map(tx => ({
        transaction_id: tx.transaction_id,
        account_id: tx.account_id,
        account_number: tx.account_number,
        account_name: tx.account_name,
        account_type: tx.account_type,
        amount: tx.amount,
        transaction_type: tx.transaction_type,
        description: tx.description
      }));

      // Calculate debit and credit sums
      let debitSum = 0;
      let creditSum = 0;
      transactions.forEach(transaction => {
        if (transaction.transaction_type === 'debit') {
          debitSum += parseFloat(transaction.amount);
        } else if (transaction.transaction_type === 'credit') {
          creditSum += parseFloat(transaction.amount);
        }
      });

      const verificationData = {
        verification_id: data.verification_id,
        date: data.date,
        verification_description: data.description,
        series: data.series,
        number: data.number,
        created_at: data.created_at,
        reference: data.reference,
        notes: data.notes,
        transactions,
        debitSum,
        creditSum
      };

      setSelectedVerification(verificationData);
      setShowVerificationDetails(true);
    } catch (error) {
      console.error('Error fetching verification details:', error);
      setError('Kunde inte hämta verifikationsdetaljer.');
    }
  };

  const handleVerificationUpdated = () => {
    // Refresh verifications list when a verification is updated
    // You can fetch the updated list here if needed
  };

  // Helper: show payment dialog with date + amount fields
  const showPaymentDialog = async (title, confirmText) => {
    const remaining = invoice.remaining_balance ?? parseFloat(invoice.amount);
    const formatAmount = (v) => new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(v);

    const result = await Swal.fire({
      title,
      html: `
        <div style="text-align:left; margin-top:0.5rem;">
          <label style="display:block; margin-bottom:0.25rem; font-size:0.875rem; font-weight:500;">Betalningsdatum</label>
          <input id="swal-payment-date" type="date" class="swal2-input" style="margin:0 0 1rem 0; width:100%;"
            value="${paymentDate}" min="${invoice.invoice_date}" max="${dayjs().format('YYYY-MM-DD')}" />

          <label style="display:block; margin-bottom:0.25rem; font-size:0.875rem; font-weight:500;">Belopp</label>
          <input id="swal-payment-amount" type="number" class="swal2-input" style="margin:0; width:100%;"
            value="${remaining.toFixed(2)}" min="0.01" step="0.01" />
          <div style="font-size:0.75rem; color:#6b7280; margin-top:0.25rem;">
            Kvarvarande: ${formatAmount(remaining)}
          </div>
        </div>
      `,
      showCancelButton: true,
      cancelButtonText: 'Avbryt',
      confirmButtonText: confirmText,
      focusConfirm: false,
      preConfirm: () => {
        const date = document.getElementById('swal-payment-date').value;
        const amount = parseFloat(document.getElementById('swal-payment-amount').value);
        if (!date) { Swal.showValidationMessage('Betalningsdatum krävs'); return false; }
        if (!amount || amount <= 0) { Swal.showValidationMessage('Beloppet måste vara större än 0'); return false; }
        if (amount > remaining + 0.01) { Swal.showValidationMessage(`Beloppet överstiger kvarvarande saldo (${formatAmount(remaining)})`); return false; }
        return { date, amount };
      }
    });

    if (result.isConfirmed) return result.value; // { date, amount }
    return null; // cancelled
  };

  const handleActionClick = async () => {
    if (nextAction.requireBankAccount) {
      // Accrual payment: single dialog with date + amount + bank account selector
      const remaining = invoice.remaining_balance ?? parseFloat(invoice.amount);
      const formatAmount = (v) => new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(v);
      const accountOptions = bankAccounts.map(a =>
        `<option value="${a.account_id}" data-number="${a.account_number}">${a.account_number} — ${a.account_name}</option>`
      ).join('');

      const result = await Swal.fire({
        title: 'Registrera betalning',
        html: `
          <div style="text-align:left; margin-top:0.5rem;">
            <label style="display:block; margin-bottom:0.25rem; font-size:0.875rem; font-weight:500;">Betalningsdatum</label>
            <input id="swal-pay-date" type="date" class="swal2-input" style="margin:0 0 1rem 0; width:100%;"
              value="${paymentDate}" min="${invoice.invoice_date}" max="${dayjs().format('YYYY-MM-DD')}" />

            <label style="display:block; margin-bottom:0.25rem; font-size:0.875rem; font-weight:500;">Belopp</label>
            <input id="swal-pay-amount" type="number" class="swal2-input" style="margin:0 0 1rem 0; width:100%;"
              value="${remaining.toFixed(2)}" min="0.01" step="0.01" />
            <div style="font-size:0.75rem; color:#6b7280; margin-bottom:1rem;">Kvarvarande: ${formatAmount(remaining)}</div>

            <label style="display:block; margin-bottom:0.25rem; font-size:0.875rem; font-weight:500;">Betalkonto</label>
            <select id="swal-pay-bank" class="swal2-select" style="margin:0; width:100%;">
              ${accountOptions || '<option value="">Inga betalkonton hittades</option>'}
            </select>
          </div>
        `,
        showCancelButton: true,
        cancelButtonText: 'Avbryt',
        confirmButtonText: 'Bekräfta betalning',
        focusConfirm: false,
        preConfirm: () => {
          const date = document.getElementById('swal-pay-date').value;
          const amount = parseFloat(document.getElementById('swal-pay-amount').value);
          const bankSelect = document.getElementById('swal-pay-bank');
          const bankAccountId = parseInt(bankSelect.value);
          const bankAccountNumber = bankSelect.selectedOptions[0]?.dataset?.number;
          if (!date) { Swal.showValidationMessage('Betalningsdatum krävs'); return false; }
          if (!amount || amount <= 0) { Swal.showValidationMessage('Beloppet måste vara större än 0'); return false; }
          if (amount > remaining + 0.01) { Swal.showValidationMessage(`Beloppet överstiger kvarvarande saldo (${formatAmount(remaining)})`); return false; }
          if (!bankAccountId) { Swal.showValidationMessage('Välj ett betalkonto'); return false; }
          return { date, amount, bankAccountId, bankAccountNumber };
        }
      });

      if (!result.isConfirmed) return;
      const { date, amount, bankAccountId, bankAccountNumber } = result.value;

      // Auto-generate transactions: debit the accounts_payable role account
      // (clear liability), credit bank — never a pinned account id.
      const apRole = await resolveRoleAccount(companyId, 'accounts_payable');
      if (!apRole) {
        toast.error('Rollen Leverantörsskulder är inte mappad — mappa den under Inställningar → Bokföring → Kontoplansroller.');
        return;
      }
      const transactions = [
        { account_id: apRole.accountId, amount: amount, transaction_type: 'debit', description: 'Leverantörsskuld reglerad' },
        { account_id: bankAccountId, amount: amount, transaction_type: 'credit', description: `Betalning via ${bankAccountNumber}` }
      ];

      setPaymentDate(date);
      handleStatusUpdate(nextAction.nextStatus, date, amount, transactions);
      return;
    }

    if (nextAction.isVerification && nextAction.requireDate) {
      // Cash payment: collect date + amount, then open VerificationModal for full account coding
      const payment = await showPaymentDialog('Registrera betalning', 'Fortsätt till kontering');
      if (!payment) return;
      setPaymentDate(payment.date);
      setPaymentAmount(payment.amount);
      setVerificationType(nextAction.verificationType);
      setShowVerificationModal(true);
    } else if (nextAction.isVerification) {
      // Accrual approval: open VerificationModal directly
      setVerificationType(nextAction.verificationType);
      setShowVerificationModal(true);
    } else {
      // Simple confirmation without date (cash approval)
      swal({
        title: 'Bekräfta åtgärd',
        text: `Är du säker på att du vill ${nextAction.text.toLowerCase()}?`,
        icon: 'info',
        buttons: ['Avbryt', `Ja, ${nextAction.text.toLowerCase()}`],
      }).then((confirmed) => {
        if (confirmed) {
          handleStatusUpdate(nextAction.nextStatus);
        }
      });
    }
  };

  const handleConfirmVerification = async (verificationData) => {
    setMarkAsPaidLoading(true);
    setError(null);
    try {
      let apiUrl;
      let payload;

      if(verificationType==='year_end') {
        apiUrl=getApiUrl(`${companyId}/purchasing/supplier-invoices/year-end/book`);
        payload={fiscalYearId:new URLSearchParams(window.location.search).get('yearEndFiscalYearId'),items:[{invoiceId,transactions:verificationData.transactions,asPeriodisation:verificationData.asPeriodisation}]};
      } else if (verificationType === 'accrual_invoice') {
        // Accrual approval: POST /approve with transactions from VerificationModal
        apiUrl = getApiUrl(
          `${companyId}/purchasing/supplier-invoices/${invoiceId}/approve`
        );
        payload = {
          transactions: verificationData.transactions,
          ...(verificationData.asPeriodisation && { asPeriodisation: verificationData.asPeriodisation }),
          date: invoice.invoice_date,
          description: `Leverantörsfaktura ${invoice.invoice_number} — ${invoice.supplier_name}`,
          // Book-as-asset (VerificationModal asset panel): backend books the
          // verifikat + creates the register asset atomically, returns assetIds.
          ...(verificationData.asAsset && { asAsset: verificationData.asAsset })
        };
      } else if (verificationType === 'cash_payment' || verificationType === 'accrual_payment') {
        // Payment: POST /pay with transactions from VerificationModal
        apiUrl = getApiUrl(
          `${companyId}/purchasing/supplier-invoices/${invoiceId}/pay`
        );
        payload = {
          payment_date: paymentDate,
          transactions: verificationData.transactions,
          ...(verificationData.asPeriodisation && { asPeriodisation: verificationData.asPeriodisation }),
          ...(paymentAmount !== null && { amount: paymentAmount }),
          ...paymentFileLink(paymentAmount),
        };
      } else {
        throw new Error(`Unknown verification type: ${verificationType}`);
      }

      const response = await axios.post(apiUrl, payload, { withCredentials: true });

      // Close the modal
      setShowVerificationModal(false);

      // Display success message. assetIds present = the accrual path also
      // created the register asset (asAsset). Absent despite asAsset (e.g.
      // cash path) = the persisted detection queue entry acts as backstop.
      if (response.data?.assetIds) {
        toast.success('Fakturan är bokförd och tillgången är upplagd — avskrivningen sköts automatiskt.');
      } else {
        toast.success('Verifikation skapad.');
      }

      // Re-fetch invoice and verifications from server for accurate state
      await Promise.all([fetchInvoice(), fetchAssociatedVerifications()]);
    } catch (error) {
      console.error('Error creating verification:', error);
      const msg = error.response?.data?.error || 'Kunde inte skapa verifikation. Vänligen försök igen senare.';
      setError(msg);
    } finally {
      setMarkAsPaidLoading(false);
    }
  };

  // Undo helpers
  const getUndoInfo = () => {
    if (!invoice) return null;
    const status = invoice.status;
    const method = accountingMethod;

    const effectiveMethod = (method === 'accrual' || (invoice.use_accrual_method || invoice.year_end_booked)) ? 'accrual' : 'cash';

    const infoMap = {
      'Approved': {
        title: 'Ångra godkännande?',
        impacts: (() => {
          const impacts = ['Fakturan återställs till Skannad'];
          if (effectiveMethod === 'accrual') {
            impacts.unshift('Verifikationen för leverantörsskulden annulleras');
          }
          return impacts;
        })(),
        level: effectiveMethod === 'accrual' ? 'warning' : 'info'
      },
      'Paid': {
        title: 'Ångra betalning?',
        impacts: ['Betalningen markeras som reverserad', 'Verifikationen annulleras', 'Fakturan återställs till Godkänd'],
        level: 'danger'
      }
    };

    return infoMap[status] || null;
  };

  const handleUndoClick = () => {
    const undoInfo = getUndoInfo();
    if (!undoInfo) return;

    const iconMap = { info: 'info', warning: 'warning', danger: 'error' };

    swal({
      title: undoInfo.title,
      text: 'Detta kommer att:\n' + undoInfo.impacts.map(i => `• ${i}`).join('\n'),
      icon: iconMap[undoInfo.level],
      buttons: ['Avbryt', 'Ångra'],
      dangerMode: undoInfo.level === 'danger',
    }).then(async (confirmed) => {
      if (!confirmed) return;

      setUndoLoading(true);
      setError(null);
      try {
        const undoApiUrl = getApiUrl(
          `${companyId}/purchasing/supplier-invoices/${invoiceId}/undo`
        );
        await axios.post(undoApiUrl, {}, { withCredentials: true });

        toast.success('Steget har ångrats.');

        // Re-fetch invoice and verifications from server
        await Promise.all([fetchInvoice(), fetchAssociatedVerifications()]);
      } catch (error) {
        console.error('Error undoing step:', error);
        const msg = error.response?.data?.error || 'Kunde inte ångra steget. Vänligen försök igen.';
        setError(msg);
      } finally {
        setUndoLoading(false);
      }
    });
  };

  // Render loading state
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Laddar...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <>
      <PageHeader
        backTo={{ to: `/${companyId}/supplier-invoices`, label: 'Alla leverantörsfakturor' }}
        icon={ShoppingCart}
        title={`Faktura ${invoice?.invoice_number ?? ''}`}
      />

      <style jsx>{`
        .modern-card {
          border: none;
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
          transition: all 0.3s ease;
        }

        .modern-card:hover {
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
        }

        .detail-item {
          background: #f8fafc;
          border-radius: 8px;
          padding: 1rem;
          margin-bottom: 0.75rem;
          border-left: 3px solid var(--bs-primary);
        }

        .detail-label {
          font-size: 0.85rem;
          font-weight: 600;
          color: #64748b;
          margin-bottom: 0.25rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .detail-value {
          font-size: 1.1rem;
          font-weight: 600;
          color: #1e293b;
        }

        .timeline-modern {
          position: relative;
          padding-left: 2rem;
        }

        .timeline-modern::before {
          content: '';
          position: absolute;
          left: 1rem;
          top: 0;
          bottom: 0;
          width: 2px;
          background: linear-gradient(to bottom, var(--bs-primary), #e2e8f0);
        }

        .timeline-item {
          position: relative;
          margin-bottom: 1.5rem;
          background: white;
          border-radius: 8px;
          padding: 1rem;
          border: 1px solid #e2e8f0;
        }

        .timeline-icon {
          position: absolute;
          left: -2.5rem;
          top: 1rem;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background: white;
          border: 2px solid var(--bs-primary);
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .attachment-card {
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          padding: 1rem;
          margin-bottom: 1rem;
          background: white;
          transition: all 0.2s ease;
        }

        .attachment-card:hover {
          border-color: var(--bs-primary);
          box-shadow: 0 2px 8px var(--rgba-primary-1);
        }

        .action-button {
          background: var(--bs-primary);
          border: none;
          padding: 0.75rem 1.5rem;
          border-radius: 8px;
          font-weight: 600;
          transition: all 0.2s ease;
        }

        .action-button:hover {
          background: #3a6f42;
          transform: translateY(-1px);
        }

        .back-button {
          background: #f1f5f9;
          color: #475569;
          border: 1px solid #e2e8f0;
          padding: 0.5rem 1rem;
          border-radius: 8px;
          font-weight: 500;
          transition: all 0.2s ease;
        }

        .back-button:hover {
          background: #e2e8f0;
          color: #334155;
        }

        /* Document Viewer */
        .doc-viewer-container {
          background: white;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
        }
        .doc-toolbar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0.75rem 1rem;
          background: #fafbfc;
          border-bottom: 1px solid #f1f5f9;
        }
        .doc-frame {
          height: calc(100vh - 200px);
          min-height: 600px;
          overflow: auto;
          background: #525659;
        }
        .doc-frame .rpv-core__viewer {
          height: 100%;
        }

        .info-strip {
          display: flex;
          align-items: center;
          background: white;
          border-radius: 12px;
          padding: 1.25rem 1.5rem;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
          gap: 1.5rem;
          margin-bottom: 1.5rem;
        }
        .info-item {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          flex: 1;
          min-width: 0;
        }
        .info-value {
          font-size: 0.9375rem;
          font-weight: 600;
          color: var(--title);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .info-label {
          font-size: 0.6875rem;
          color: var(--secondary);
          text-transform: uppercase;
          letter-spacing: 0.03em;
        }
        .info-divider {
          width: 1px;
          height: 2rem;
          background: #e2e8f0;
          flex-shrink: 0;
        }
        @media (max-width: 768px) {
          .info-strip {
            flex-direction: column;
            gap: 1rem;
            align-items: flex-start;
          }
          .info-divider {
            width: 100%;
            height: 1px;
          }
        }

        @media (max-width: 768px) {

          .stat-value {
            font-size: 1.5rem;
          }
        }

        /* Step Indicator — matches payroll stepper style */
        .si-step-indicator {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 40px;
          height: 40px;
          border-radius: 50%;
          font-weight: 600;
          font-size: 0.875rem;
          transition: all 0.3s ease;
          flex-shrink: 0;
        }

        .si-step-indicator.completed {
          background: var(--bs-success);
          color: white;
        }

        .si-step-indicator.current {
          background: var(--bs-primary);
          color: white;
          animation: si-pulse 2s infinite;
        }

        .si-step-indicator.pending {
          background: var(--step-bg-muted, #f3f4f6);
          color: var(--step-text-muted, #9ca3af);
        }

        @keyframes si-pulse {
          0% {
            box-shadow: 0 0 0 0 var(--rgba-primary-4);
          }
          70% {
            box-shadow: 0 0 0 10px transparent;
          }
          100% {
            box-shadow: 0 0 0 0 transparent;
          }
        }

        .si-step-block {
          display: flex;
          align-items: center;
        }

        .si-step-label {
          font-weight: 600;
          font-size: 0.875rem;
          color: var(--step-text-heading, #1e293b);
          transition: color 0.2s ease;
        }

        .si-step-sublabel {
          font-size: 0.75rem;
          color: var(--step-text-muted, #64748b);
        }

        .si-step-block.completed .si-step-label {
          color: var(--step-success-text-deep, #166534);
        }

        .si-step-block.current .si-step-label {
          color: var(--bs-primary);
        }

        .si-step-block.pending .si-step-label {
          color: var(--step-text-disabled, #9ca3af);
        }

        .si-step-block.pending .si-step-sublabel {
          color: var(--step-border-disabled, #d1d5db);
        }

        .step-connector {
          flex: 1;
          height: 2px;
          background: var(--step-border-light, #e5e7eb);
          margin: 0 0.5rem;
          min-width: 20px;
        }

        .step-connector.completed {
          background: var(--bs-success);
        }

        .undo-step-link {
          display: inline-flex;
          align-items: center;
          font-size: 0.7rem;
          color: #dc3545;
          cursor: pointer;
          margin-top: 2px;
          background: none;
          border: none;
          padding: 0;
          text-decoration: none;
        }

        .undo-step-link:hover {
          text-decoration: underline;
          color: #a71d2a;
        }

        /* SweetAlert buttons — use project theme colors */
        .swal-button--confirm {
          background-color: var(--primary);
        }
        .swal-button--confirm:hover {
          background-color: var(--primary-hover) !important;
        }
        .swal-button--cancel {
          color: var(--secondary);
        }
        .swal-button--danger {
          background-color: var(--bs-red, #EE3232);
        }
        .swal-button--danger:hover {
          background-color: #c82333 !important;
        }

        @media (max-width: 768px) {
          .si-steps-row {
            flex-direction: column !important;
            gap: 0.5rem;
          }

          .si-step-block {
            background: var(--step-bg, #fff);
            border-radius: 8px;
            border: 1px solid var(--step-border-light, #e5e7eb);
            padding: 0.75rem 1rem;
          }

          .si-step-indicator {
            width: 36px;
            height: 36px;
          }

          .step-connector {
            display: none;
          }
        }
      `}</style>

      {/* Display error messages */}
      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible className="modern-card mb-4">
          <AlertCircle size={20} className="me-2" />
          {error}
        </Alert>
      )}


      {/* Check if invoice exists */}
      {!invoice ? (
        <Alert variant="warning" className="modern-card">
          <AlertTriangle size={20} className="me-2" />
          Fakturan kunde inte hittas.
        </Alert>
      ) : (
        <>
          {/* Bank details read from the document vs the supplier's active method (plan UC7) */}
          <PaymentMethodProposalBanner companyId={companyId} invoice={invoice} onChanged={fetchInvoice} />

          {/* Metadata + actions bar — the back link and title live in PageHeader above;
              this row keeps what the masthead doesn't carry: supplier/date chips and
              the invoice's action buttons. */}
          <div className="mb-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                <div className="d-flex align-items-center gap-3 flex-wrap">
                  <div className="d-flex align-items-center gap-2 text-muted">
                    <Building size={16} />
                    <span style={{ fontWeight: '500' }}>{invoice.supplier_name}</span>
                  </div>
                  {(() => {
                    // Derived from the live payment-file row; never stored on the invoice.
                    const chip = paymentFileChip(invoice);
                    if (!chip) return null;
                    const href = invoice.payment_file_id ? `/${companyId}/payments?tab=files&file=${invoice.payment_file_id}` : `/${companyId}/payments`;
                    return <Link to={href} style={{ textDecoration: 'none' }}><StatusBadge tone={chip.tone}>{chip.label}</StatusBadge></Link>;
                  })()}
                  {(() => {
                    // Booked by the automatic booking policy (an auto_booked decision exists).
                    const chip = autoBookingInvoiceBadge(invoice);
                    return chip ? <StatusBadge tone={chip.tone}>{chip.label}</StatusBadge> : null;
                  })()}
                  {invoice.payment_reference && (
                    <div className="d-flex align-items-center gap-2" style={{ padding: '0.25rem 0.75rem', background: '#f8fafc', borderRadius: '6px', fontSize: '0.8125rem' }}>
                      <Hash size={14} className="text-muted" />
                      <span className="text-muted">{invoice.reference_scheme === 'se_ocr' ? 'OCR:' : 'Referens:'}</span>
                      <span style={{ fontWeight: '600', color: '#1e293b', fontVariantNumeric: 'tabular-nums' }}>{invoice.payment_reference}</span>
                    </div>
                  )}
                  <div className="d-flex align-items-center gap-2" style={{
                    padding: '0.25rem 0.75rem',
                    background: '#f8fafc',
                    borderRadius: '6px',
                    fontSize: '0.8125rem'
                  }}>
                    <CalendarDays size={14} className="text-muted" />
                    <span className="text-muted">Fakturadatum:</span>
                    <span style={{ fontWeight: '600', color: '#1e293b' }}>
                      {dayjs(invoice.invoice_date).format('DD MMM YYYY')}
                    </span>
                  </div>
                  {invoice.service_period_start && invoice.service_period_end && (
                    <div className="d-flex align-items-center gap-2" style={{ padding: '0.25rem 0.75rem', background: '#f8fafc', borderRadius: '6px', fontSize: '0.8125rem' }}>
                      <CalendarDays size={14} className="text-muted" />
                      <span className="text-muted">Tjänsteperiod:</span>
                      <span style={{ fontWeight: '600', color: '#1e293b' }}>
                        {dayjs(invoice.service_period_start).format('DD MMM YYYY')} – {dayjs(invoice.service_period_end).format('DD MMM YYYY')}
                      </span>
                      {invoice.periodisation_plan_id && (
                        <Link to={`/${companyId}/periodisering`} style={{ fontSize: '0.75rem' }}>Periodiseringsplan</Link>
                      )}
                    </div>
                  )}
                  {/* Show payment date if paid, otherwise show due date */}
                  {(invoice.status === 'Paid') && invoice.payment_date ? (
                    <div className="d-flex align-items-center gap-2" style={{
                      padding: '0.25rem 0.75rem',
                      background: '#f0fdf4',
                      borderRadius: '6px',
                      fontSize: '0.8125rem'
                    }}>
                      <CheckCircle2 size={14} style={{ color: 'var(--bs-success)' }} />
                      <span className="text-muted">Betald:</span>
                      <span style={{ fontWeight: '600', color: 'var(--bs-success)' }}>
                        {dayjs(invoice.payment_date).format('DD MMM YYYY')}
                      </span>
                    </div>
                  ) : (
                    <div className="d-flex align-items-center gap-2" style={{
                      padding: '0.25rem 0.75rem',
                      background: daysUntilDue < 0 ? '#fef2f2' : daysUntilDue <= 7 ? '#fffbeb' : '#f0fdf4',
                      borderRadius: '6px',
                      fontSize: '0.8125rem'
                    }}>
                      <Timer size={14} style={{
                        color: daysUntilDue < 0 ? 'var(--bs-danger)' : daysUntilDue <= 7 ? 'var(--bs-warning)' : 'var(--bs-success)'
                      }} />
                      <span className="text-muted">Förfaller:</span>
                      <span style={{
                        fontWeight: '600',
                        color: daysUntilDue < 0 ? 'var(--bs-danger)' : daysUntilDue <= 7 ? 'var(--bs-warning)' : 'var(--bs-success)'
                      }}>
                        {dayjs(invoice.due_date).format('DD MMM YYYY')}
                      </span>
                      {daysUntilDue < 0 && (
                        <Badge bg="danger" style={{ fontSize: '0.625rem', marginLeft: '0.25rem' }}>
                          {Math.abs(daysUntilDue)}d försenad
                        </Badge>
                      )}
                      {daysUntilDue >= 0 && daysUntilDue <= 7 && (
                        <Badge bg="warning" style={{ fontSize: '0.625rem', marginLeft: '0.25rem' }}>
                          {daysUntilDue}d kvar
                        </Badge>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className="d-flex align-items-center gap-3">
                {/* Leverantörsskuld indicator — shown when accrual override is active */}
                {invoice.use_accrual_method ? (
                  <OverlayTrigger
                    placement="bottom"
                    overlay={
                      <Tooltip>
                        Använder fakturametoden för denna faktura — skapar först en verifikation för leverantörsskulden (2440), sedan en för betalningen
                      </Tooltip>
                    }
                  >
                    <div
                      className="d-flex align-items-center gap-1"
                      style={{
                        padding: '0.25rem 0.625rem',
                        borderRadius: '1rem',
                        backgroundColor: '#eff6ff',
                        fontSize: '0.75rem',
                        fontWeight: '500',
                        color: '#1e40af'
                      }}
                    >
                      <Info size={12} />
                      Bokförs som lev.skuld
                    </div>
                  </OverlayTrigger>
                ) : null}

                {/* Contextual assistant hand-off — sits with the invoice's actions */}
                {hasAiAssistant && taskDescriptor && (
                  <Button variant="outline-primary" onClick={handleAskAssistant}>
                    Fråga assistenten
                  </Button>
                )}

                {/* Payment file (ISO 20022): queue an approved invoice; admin + entitlement */}
                {Boolean(features?.payment_files) && isAdmin && invoice.status === 'Approved' && !invoice.payment_instruction_status && (
                  invoice.supplier_payment_methods?.active ? (
                    <Button variant="outline-primary" onClick={handleQueueForPayment} disabled={queueBusy}>
                      {queueBusy ? <Spinner size="sm" /> : 'Lägg i betalningskö'}
                    </Button>
                  ) : (
                    <OverlayTrigger placement="bottom" overlay={<Tooltip>Leverantören saknar aktiva betalningsuppgifter. Lägg till och bekräfta dem på leverantörskortet.</Tooltip>}>
                      <span>
                        <Button variant="outline-secondary" onClick={() => history.push(`/${companyId}/suppliers/${invoice.supplier_id}`)}>
                          Betalningsuppgifter saknas
                        </Button>
                      </span>
                    </OverlayTrigger>
                  )
                )}

                {/* Primary action button */}
                {isAdmin && invoice.status==='Approved' && accountingMethod==='cash' && !invoice.year_end_booked && new URLSearchParams(window.location.search).get('yearEndFiscalYearId') && <Button onClick={()=>{setVerificationType('year_end');setShowVerificationModal(true);}}>Bokför vid bokslut</Button>}
                {nextAction && (
                  <Button
                    className="action-button d-flex align-items-center gap-2"
                    onClick={handleActionClick}
                    disabled={markAsPaidLoading}
                  >
                    {markAsPaidLoading ? (
                      <>
                        <Spinner size="sm" />
                        Uppdaterar...
                      </>
                    ) : (
                      <>
                        {nextAction.nextStatus === 'Approved' && <CheckCircle size={16} />}
                        {nextAction.nextStatus === 'Paid' && <CreditCard size={16} />}
                        {nextAction.text}
                      </>
                    )}
                  </Button>
                )}

                {/* Cogwheel dropdown for secondary actions */}
                <Dropdown align="end">
                  <Dropdown.Toggle
                    variant="outline-secondary"
                    className="d-flex align-items-center justify-content-center"
                    style={{ width: '38px', height: '38px', padding: 0 }}
                    id="invoice-actions-dropdown"
                  >
                    <Settings size={16} />
                  </Dropdown.Toggle>
                  <Dropdown.Menu style={{ minWidth: '240px' }}>
                    {/* Edit */}
                    <Dropdown.Item
                      className="d-flex align-items-center gap-2 py-2"
                      onClick={() => history.push(`/${companyId}/edit-supplier-invoice/${invoiceId}`)}
                      disabled={invoice.status !== 'Scanned'}
                      style={{ fontSize: '0.875rem' }}
                    >
                      <Edit size={15} />
                      Redigera faktura
                      {invoice.status !== 'Scanned' && (
                        <span style={{ fontSize: '0.6875rem', color: '#9ca3af', marginLeft: 'auto' }}>
                          Ej tillgänglig
                        </span>
                      )}
                    </Dropdown.Item>

                    {/* Bokför som leverantörsskuld toggle — shown when invoice's fiscal year uses cash method */}
                    {(accountingMethod === 'cash' || (invoice.use_accrual_method || invoice.year_end_booked)) && (
                      <>
                        <Dropdown.Divider />
                        {(() => {
                          const hasActivePayments = invoice.payments && invoice.payments.some(p => !p.reversed);
                          const hasVerifications = invoice.has_verification || (invoice.statusHistory || []).some(h => h.status === 'Approved' && h.change_type === 'forward');
                          const isLocked = invoice.status === 'Paid' || hasActivePayments || (invoice.status === 'Approved' && hasVerifications);
                          return (
                            <Dropdown.Item
                              as="div"
                              className="d-flex align-items-center justify-content-between py-2 px-3"
                              style={{ cursor: isLocked ? 'not-allowed' : 'pointer', opacity: isLocked ? 0.5 : 1 }}
                              onClick={async (e) => {
                                e.stopPropagation();
                                if (isLocked) return;
                                try {
                                  const newValue = !invoice.use_accrual_method;
                                  await axios.put(
                                    getApiUrl(`${companyId}/purchasing/supplier-invoices/${invoiceId}`),
                                    { use_accrual_method: newValue },
                                    { withCredentials: true }
                                  );
                                  const response = await axios.get(
                                    getApiUrl(`${companyId}/purchasing/supplier-invoices/${invoiceId}`),
                                    { withCredentials: true }
                                  );
                                  setInvoice(response.data);
                                } catch (err) {
                                  console.error('Error toggling accrual method:', err);
                                  setError('Kunde inte ändra bokföringsmetod.');
                                }
                              }}
                            >
                              <div className="d-flex align-items-center gap-2">
                                <span style={{ fontSize: '0.875rem' }}>Bokför som lev.skuld</span>
                                <OverlayTrigger
                                  placement="left"
                                  overlay={
                                    <Tooltip>
                                      {isLocked
                                        ? 'Kan inte ändras — ångra till Skannad först för att byta bokföringsmetod'
                                        : 'Bokför som leverantörsskuld (konto 2440) och reglera vid betalning'}
                                    </Tooltip>
                                  }
                                >
                                  <Info size={13} style={{ color: '#9ca3af', cursor: 'help' }} />
                                </OverlayTrigger>
                              </div>
                              <div style={{
                                width: '36px',
                                height: '20px',
                                borderRadius: '10px',
                                backgroundColor: invoice.use_accrual_method ? '#3b82f6' : '#d1d5db',
                                position: 'relative',
                                transition: 'background-color 0.2s ease',
                                flexShrink: 0
                              }}>
                                <div style={{
                                  width: '16px',
                                  height: '16px',
                                  borderRadius: '50%',
                                  backgroundColor: '#fff',
                                  position: 'absolute',
                                  top: '2px',
                                  left: invoice.use_accrual_method ? '18px' : '2px',
                                  transition: 'left 0.2s ease',
                                  boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                                }} />
                              </div>
                            </Dropdown.Item>
                          );
                        })()}
                      </>
                    )}
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            </div>
          </div>

          {/* Key Info Strip */}
          <div className="info-strip">
            <div className="info-item">
              <Banknote size={18} style={{ color: 'var(--bs-cyan)', opacity: 0.8 }} />
              <div>
                <div className="info-value" style={{ color: 'var(--title)' }}>
                  {new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(parseFloat(invoice.amount) || 0)}
                </div>
                <div className="info-label">Belopp</div>
              </div>
            </div>

            <div className="info-divider" />

            <div className="info-item">
              {(() => {
                const { icon: IconComponent, color } = getStatusIcon(invoice.status);
                return <IconComponent size={18} style={{ color, opacity: 0.8 }} />;
              })()}
              <div>
                <div className="info-value" style={{
                  color: invoice.status === 'Paid' ? 'var(--bs-success)' : 'var(--title)'
                }}>
                  {getStatusDisplayName(invoice.status)}
                </div>
                <div className="info-label">Status</div>
              </div>
            </div>

            <div className="info-divider" />

            <div className="info-item">
              {(invoice.status === 'Paid') && invoice.payment_date ? (
                <>
                  <CheckCircle2 size={18} style={{ color: 'var(--bs-success)', opacity: 0.8 }} />
                  <div>
                    <div className="info-value" style={{ color: 'var(--bs-success)' }}>
                      {dayjs(invoice.payment_date).format('DD MMM YYYY')}
                    </div>
                    <div className="info-label">Betald</div>
                  </div>
                </>
              ) : (
                <>
                  {daysUntilDue < 0 ? <AlertTriangle size={18} style={{ color: 'var(--bs-danger)', opacity: 0.8 }} />
                    : <Clock size={18} style={{ color: daysUntilDue <= 7 ? 'var(--bs-warning)' : 'var(--secondary)', opacity: 0.8 }} />}
                  <div>
                    <div className="info-value" style={{
                      color: daysUntilDue < 0 ? 'var(--bs-danger)' : daysUntilDue <= 7 ? 'var(--bs-warning)' : 'var(--title)'
                    }}>
                      {daysUntilDue < 0 ? `${Math.abs(daysUntilDue)}d försenad` : daysUntilDue === 0 ? 'Förfaller idag' : `${daysUntilDue}d kvar`}
                    </div>
                    <div className="info-label">{dayjs(invoice.due_date).format('DD MMM YYYY')}</div>
                  </div>
                </>
              )}
            </div>

            <div className="info-divider" />

            <div className="info-item">
              <Building size={18} style={{ color: '#8b5cf6', opacity: 0.8 }} />
              <div>
                <div className="info-value">{invoice.supplier_name}</div>
                <div className="info-label">{invoice.supplier_orgnumber || 'Leverantör'}</div>
              </div>
            </div>
          </div>

          {/* Processing History - Step Indicator */}
          <Card className="modern-card mb-4">
            <Card.Header className="bg-white border-bottom">
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0 d-flex align-items-center gap-2">
                  <Clock size={20} className="text-primary" />
                  Behandlingshistorik
                </h5>
              </div>
            </Card.Header>
            <Card.Body className="p-4">
              {(() => {
                // 3-state workflow: Scanned → Approved → Paid
                const allSteps = [
                  { status: 'Scanned', label: 'Skannad', icon: FileText },
                  { status: 'Approved', label: 'Godkänd', icon: CheckCircle },
                  { status: 'Paid', label: 'Betald & bokförd', icon: CreditCard }
                ];

                // Create a map of completed statuses with their timestamps
                // Only use 'forward' entries — undo and superseded entries
                // are excluded so rolled-back steps don't show stale timestamps
                const statusMap = {};
                invoice.statusHistory?.forEach(event => {
                  if (!event.change_type || event.change_type === 'forward') {
                    statusMap[event.status] = event.changed_at;
                  }
                });

                // Find current step index
                const currentStepIndex = allSteps.findIndex(step => step.status === invoice.status);

                const getStepStatus = (index) => {
                  if (index < currentStepIndex) return 'completed';
                  if (index === currentStepIndex) return 'current';
                  return 'pending';
                };

                return (
                  <div className="d-flex align-items-center justify-content-between si-steps-row">
                    {allSteps.map((step, index) => {
                      const stepStatus = getStepStatus(index);
                      const IconComponent = step.icon;
                      // Only show timestamp for completed or current steps
                      const timestamp = stepStatus !== 'pending' ? statusMap[step.status] : null;

                      return (
                        <React.Fragment key={step.status}>
                          <div className={`si-step-block ${stepStatus}`}>
                            <div className={`si-step-indicator ${stepStatus}`}>
                              {stepStatus === 'completed' ? (
                                <CheckCircle size={20} />
                              ) : (
                                <IconComponent size={18} />
                              )}
                            </div>
                            <div className="ms-3">
                              <div className="si-step-label">
                                {step.label}
                              </div>
                              {timestamp && (
                                <div className="si-step-sublabel">
                                  {dayjs(timestamp).format('DD MMM YYYY, HH:mm')}
                                </div>
                              )}
                              {index === currentStepIndex && index > 0 && (
                                <button
                                  type="button"
                                  className="undo-step-link"
                                  onClick={handleUndoClick}
                                  disabled={undoLoading}
                                >
                                  <Undo2 size={12} style={{ marginRight: 3 }} />
                                  {undoLoading ? 'Ångrar...' : 'Ångra'}
                                </button>
                              )}
                            </div>
                          </div>
                          {index < allSteps.length - 1 && (
                            <div className={`step-connector ${index < currentStepIndex ? 'completed' : ''}`}></div>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </div>
                );
              })()}

              {/* Verification Links */}
              {verifications && verifications.length > 0 && (
                <div className="mt-4 pt-3 border-top">
                  <div className="mb-2" style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Kopplade Verifikationer
                  </div>
                  <div className="d-flex gap-2 flex-wrap">
                    {verifications.map((verification) => {
                      const isAnnulled = verification.reference_status === 'ANNULLED';
                      const isAnnulment = !!verification.annuls_verification_id;

                      return (
                        <Badge
                          key={verification.verification_id}
                          bg="light"
                          text="dark"
                          className="d-inline-flex align-items-center gap-2 py-2 px-3"
                          style={{
                            fontSize: '0.8125rem',
                            border: `1px solid ${isAnnulled ? '#fecaca' : isAnnulment ? '#fed7aa' : '#e2e8f0'}`,
                            transition: 'all 0.2s ease',
                            cursor: 'pointer',
                            opacity: isAnnulled ? 0.6 : 1,
                            textDecoration: isAnnulled ? 'line-through' : 'none',
                            background: isAnnulment ? '#fff7ed' : undefined
                          }}
                          onClick={() => handleViewVerification(verification)}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.borderColor = isAnnulled ? '#f87171' : isAnnulment ? '#fb923c' : 'var(--bs-primary)';
                            e.currentTarget.style.background = isAnnulled ? '#fef2f2' : isAnnulment ? '#fff7ed' : '#f0fdf4';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.borderColor = isAnnulled ? '#fecaca' : isAnnulment ? '#fed7aa' : '#e2e8f0';
                            e.currentTarget.style.background = isAnnulment ? '#fff7ed' : '#f8f9fa';
                          }}
                        >
                          <Receipt size={14} style={{ color: isAnnulled ? '#ef4444' : isAnnulment ? '#f97316' : 'var(--bs-primary)' }} />
                          <span style={{ color: isAnnulled ? '#ef4444' : isAnnulment ? '#f97316' : 'var(--bs-primary)', fontWeight: '600' }}>
                            {isAnnulment ? 'Annulering · ' : ''}{verification.series}{verification.number}
                          </span>
                          <ExternalLink size={12} style={{ color: '#64748b' }} />
                        </Badge>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Bank reconciliation */}
              {(bankHistory.items.length > 0 || bankHistory.error) && (
                <div className="mt-4 pt-3 border-top">
                  <div className="mb-2" style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Bankavstämning
                  </div>
                  {bankHistory.error && (
                    <div role="alert" style={{ fontSize: '0.8125rem', color: '#b45309' }}>{bankHistory.error}</div>
                  )}
                  {bankHistory.items.map((item) => (
                    <div
                      key={item.action_id}
                      className="d-flex flex-wrap align-items-baseline"
                      style={{ fontSize: '0.8125rem', columnGap: 14, rowGap: 2, marginBottom: 6 }}
                    >
                      <span style={{ color: item.undo_action_id ? '#9ca3af' : 'var(--title)', fontWeight: 500 }}>
                        {describeOnInvoice(item)}
                      </span>
                      <span style={{ color: '#64748b' }}>
                        {[item.result.committed_at && dayjs(item.result.committed_at).format('DD MMM YYYY, HH:mm'), item.undo_action_id && 'Ångrad'].filter(Boolean).join(' · ')}
                      </span>
                      {bankLineHref(companyId, item) && (
                        <Link to={bankLineHref(companyId, item)} style={{ textDecoration: 'none' }}>Visa bankhändelse</Link>
                      )}
                      {canUndoBankAction(item) && (
                        <>
                          <Button variant="link" size="sm" className="p-0 text-muted" style={{ fontSize: '0.8125rem', textDecoration: 'none' }} disabled={bankHistory.busy} onClick={() => bankHistory.prepare(item, 'unlink')}>
                            Ta bort bankkoppling
                          </Button>
                          {item.result.created_verification && (
                            <Button variant="link" size="sm" className="p-0 text-muted" style={{ fontSize: '0.8125rem', textDecoration: 'none' }} disabled={bankHistory.busy} onClick={() => bankHistory.prepare(item, 'reverse')}>
                              Återför bokföringen
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </Card.Body>
          </Card>
          {bankHistory.modal}

          <Row>
            {/* Main Content - Invoice Document Viewer */}
            <Col xs={12}>
              {/* Attachments Section - Primary Focus */}
              {invoice.attachments && invoice.attachments.length > 0 && invoice.attachments[0].attachment_id ? (
                <div className="doc-viewer-container">
                  {invoice.attachments.map((attachment, index) => (
                    <div key={attachment.attachment_id} className={index > 0 ? 'mt-4' : ''}>
                      {/* Compact file toolbar */}
                      <div className="doc-toolbar">
                        <div className="d-flex align-items-center gap-2">
                          {attachment.file_type === 'application/pdf' ? (
                            <File size={15} style={{ color: '#ef4444' }} />
                          ) : attachment.file_type.startsWith('image/') ? (
                            <FileImage size={15} style={{ color: 'var(--bs-cyan)' }} />
                          ) : (
                            <File size={15} style={{ color: 'var(--secondary)' }} />
                          )}
                          <span style={{ fontSize: '0.8125rem', fontWeight: '500', color: 'var(--title)' }}>
                            {attachment.file_name}
                          </span>
                          {invoice.attachments.length > 1 && (
                            <span style={{ fontSize: '0.6875rem', color: 'var(--secondary)' }}>
                              ({index + 1} av {invoice.attachments.length})
                            </span>
                          )}
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          {attachmentUrls[attachment.attachment_id] && (
                            <>
                              <Button
                                size="sm"
                                variant="light"
                                href={attachmentUrls[attachment.attachment_id]}
                                target="_blank"
                                className="d-flex align-items-center gap-1"
                                style={{ fontSize: '0.75rem', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                              >
                                <Eye size={13} />
                                Öppna
                              </Button>
                              <Button
                                size="sm"
                                variant="light"
                                href={attachmentUrls[attachment.attachment_id]}
                                download={attachment.file_name}
                                className="d-flex align-items-center gap-1"
                                style={{ fontSize: '0.75rem', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                              >
                                <Download size={13} />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Document viewer */}
                      {attachment.file_type === 'application/pdf' && attachmentUrls[attachment.attachment_id] ? (
                        <div className="doc-frame">
                          <Worker workerUrl="https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js">
                            <Viewer fileUrl={attachmentUrls[attachment.attachment_id]} />
                          </Worker>
                        </div>
                      ) : attachment.file_type.startsWith('image/') && attachmentUrls[attachment.attachment_id] ? (
                        <div className="doc-frame" style={{ height: 'auto', padding: '1rem', display: 'flex', justifyContent: 'center' }}>
                          <img
                            src={attachmentUrls[attachment.attachment_id]}
                            alt={attachment.file_name}
                            style={{ maxWidth: '100%', height: 'auto', borderRadius: '4px' }}
                          />
                        </div>
                      ) : !attachmentUrls[attachment.attachment_id] ? (
                        <div className="doc-frame" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '0.5rem' }}>
                          <Spinner animation="border" size="sm" />
                          <span style={{ fontSize: '0.8125rem', color: 'var(--secondary)' }}>Laddar dokument...</span>
                        </div>
                      ) : null}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="doc-viewer-container" style={{ textAlign: 'center', padding: '3rem 1rem' }}>
                  <FileImage size={40} style={{ color: '#d1d5db', marginBottom: '0.75rem' }} />
                  <p style={{ color: 'var(--secondary)', fontSize: '0.875rem', margin: 0 }}>Ingen bifogad faktura</p>
                </div>
              )}
            </Col>
          </Row>

          {/* Verification Modal */}
          <VerificationModal
            show={showVerificationModal}
            onHide={() => setShowVerificationModal(false)}
            companyId={companyId}
            invoice={invoice}
            invoiceId={invoiceId}
            onConfirm={handleConfirmVerification}
            isLoading={markAsPaidLoading}
            error={error}
            setError={setError}
            verificationType={verificationType}
            overrideAmount={paymentAmount}
          />

          {/* Verification Details Modal */}
          {selectedVerification && (
            <VerificationDetailsModal
              showVerificationDetails={showVerificationDetails}
              setShowVerificationDetails={setShowVerificationDetails}
              selectedRow={selectedVerification}
              onVerificationUpdated={handleVerificationUpdated}
              viewOnly={true}
            />
          )}

        </>
      )}
    </>
  );
};

export default ViewSupplierInvoice;
